import { Link } from 'react-router-dom'
import { Timer } from 'lucide-react'
import InfoTooltip from './InfoTooltip'
import { fmtUk, parseInstant } from '../lib/reportingWindow'
import { LATE_AFTER_DAYS, type MonthStanding } from '../lib/lateReports'

const monthName = (key: string) => {
  const [y, m] = key.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

const pill = (n: number, tone: 'green' | 'red' | 'grey' | 'blue') => {
  const cls = {
    green: 'bg-[#00843D]/10 text-[#00843D]',
    red: 'bg-[#EF3340]/10 text-[#B8242E]',
    grey: 'bg-slate-100 text-slate-500',
    blue: 'bg-[#00ACED]/10 text-[#0077A8]',
  }[tone]
  return `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${n === 0 ? 'bg-slate-50 text-slate-300' : cls}`
}

/**
 * How promptly the month's status reports came in. A report is late when it
 * lands LATE_AFTER_DAYS or more days after the due date. Reads only; the
 * reports themselves are in the table below.
 */
export default function ReportingDiscipline({ current }: { current: MonthStanding }) {
  const late = current.rows.filter(r => r.timing === 'late')
  const owed = current.rows.length

  return (
    <div className="bg-white rounded-2xl border border-slate-200 mb-4">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-100">
        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Timer className="w-4 h-4 text-[#005EB8]" />
          Reporting discipline
          <InfoTooltip>
            <strong>Late status reports</strong>
            <p>A report is late when it is submitted <strong>{LATE_AFTER_DAYS} or more days</strong> after the month's due date. The day after the due date is not late.</p>
            <p>Not filed means the due date has passed and nothing has been submitted. A month with no due date cannot have a late report.</p>
            <p className="text-slate-300">An extension moves a project's lock, not the due date, so it does not change this.</p>
          </InfoTooltip>
        </h2>
        <span className="text-xs text-slate-400">
          {monthName(current.month)}{current.dueAt ? <> · due {fmtUk(parseInstant(current.dueAt))}</> : ' · no due date set'}
        </span>
      </div>

      <div className="px-4 py-3">
        <div className="min-w-0">
          {owed === 0 ? (
            <p className="text-xs text-slate-400">No status reports are owed this month.</p>
          ) : !current.dueAt ? (
            <p className="text-xs text-slate-400">{owed} report{owed !== 1 ? 's' : ''} owed. Set a due date in Settings for lateness to count.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={pill(current.onTime, 'green')}>{current.onTime} on time</span>
                <span className={pill(current.late, 'red')}>{current.late} late</span>
                <span className={pill(current.notFiled, 'grey')}>{current.notFiled} not filed</span>
                {current.pending > 0 && <span className={pill(current.pending, 'blue')}>{current.pending} pending</span>}
                <span className="text-xs text-slate-400 ml-1">of {owed}</span>
              </div>
              {late.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                  {late.map(r => (
                    <li key={r.project.id} className="text-xs">
                      <Link to={`/projects/${r.project.id}/report?month=${current.month}`}
                            className="font-medium text-slate-700 hover:text-[#005EB8]">
                        {r.project.name}
                      </Link>
                      <span className="text-[#B8242E] font-semibold"> · {r.daysAfterDue}d late</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
